import { create } from 'zustand'
import { supabase, supabaseConfigured } from '@/lib/supabase'

/**
 * Auth state — backed by Supabase Auth.
 *
 * The Supabase client itself persists the session to localStorage under
 * `rd_supabase_session`. This zustand store mirrors the session in-memory so
 * components can render reactively, and exposes a small typed API for sign-in,
 * sign-up, sign-out, and Google OAuth.
 *
 * Initialisation:
 *   - bootstrap() reads the existing session on app boot (so the user stays
 *     signed in across reloads).
 *   - Supabase's auth-state listener (registered in App.jsx) keeps the store
 *     in sync when the access token is refreshed or the user signs out from
 *     another tab.
 */

function deriveUser(supabaseUser) {
  if (!supabaseUser) return null
  const meta = supabaseUser.user_metadata || {}
  const name =
    meta.full_name ||
    meta.name ||
    [meta.given_name, meta.family_name].filter(Boolean).join(' ').trim() ||
    (supabaseUser.email || '').split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  const role = meta.role || 'doctor'
  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    name,
    role,
    avatarUrl: meta.avatar_url || meta.picture || null,
    provider: supabaseUser.app_metadata?.provider || 'email',
  }
}

export const useAuthStore = create((set, get) => ({
  user: null,
  token: null,
  ready: false,
  unreachable: false,
  configured: supabaseConfigured,

  /**
   * Hydrate the store from the existing Supabase session (if any).
   *
   * Wrapped in a 4-second hard timeout so a Supabase outage (project paused,
   * DNS failure, no internet) can never freeze the splash screen indefinitely.
   * If the timeout fires we proceed with `user: null` — the user simply sees
   * the Login page and any subsequent sign-in attempt will surface the real
   * connectivity error via the normal toast flow.
   */
  bootstrap: async () => {
    if (!supabaseConfigured) {
      set({ ready: true })
      return
    }

    const TIMEOUT_MS = 4000
    const timeoutPromise = new Promise((resolve) =>
      setTimeout(() => resolve({ __timedOut: true }), TIMEOUT_MS),
    )

    try {
      const result = await Promise.race([
        supabase.auth.getSession(),
        timeoutPromise,
      ])

      if (result?.__timedOut) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Radiance] Supabase bootstrap timed out after ' +
            TIMEOUT_MS +
            'ms — proceeding without a session. ' +
            'Check that the Supabase project is reachable: ' +
            (import.meta.env.VITE_SUPABASE_URL || '(VITE_SUPABASE_URL not set)'),
        )
        set({ user: null, token: null, ready: true, unreachable: true })
        return
      }

      const session = result?.data?.session || null
      set({
        user: deriveUser(session?.user || null),
        token: session?.access_token || null,
        ready: true,
        unreachable: false,
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Radiance] Supabase bootstrap failed:', err?.message || err)
      set({ user: null, token: null, ready: true, unreachable: true })
    }
  },

  /** Internal — called by the Supabase auth-state listener. */
  setSession: (session) => {
    set({
      user: deriveUser(session?.user || null),
      token: session?.access_token || null,
      ready: true,
      unreachable: false,
    })
  },

  /** Email + password sign-in. */
  signIn: async ({ email, password }) => {
    if (!supabaseConfigured) {
      throw new Error('Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env')
    }
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    })
    if (error) throw error
    get().setSession(data.session)
    return data.session
  },

  /** Email + password sign-up. Sends a confirmation email by default. */
  signUp: async ({ email, password, name }) => {
    if (!supabaseConfigured) {
      throw new Error('Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env')
    }
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: { full_name: name?.trim() || undefined },
        emailRedirectTo: window.location.origin,
      },
    })
    if (error) throw error
    // If email confirmation is disabled, a session is returned immediately.
    if (data.session) get().setSession(data.session)
    return data
  },

  /** Google OAuth — pops a redirect to Google then comes back with a session. */
  signInWithGoogle: async () => {
    if (!supabaseConfigured) {
      throw new Error('Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env')
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    })
    if (error) throw error
    // The browser is being redirected; the resolved Promise here is not
    // particularly useful — the next page-load picks up the session via
    // detectSessionInUrl.
  },

  /** Send a password reset email. */
  resetPassword: async (email) => {
    if (!supabaseConfigured) {
      throw new Error('Supabase is not configured.')
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin + '/login',
    })
    if (error) throw error
  },

  /** Sign out and clear local state. */
  logout: async () => {
    try {
      await supabase.auth.signOut()
    } catch {
      /* best-effort */
    }
    set({ user: null, token: null })
  },
}))
